<template>
  <v-container class="mt-0 pt-0">
    <v-row v-if="clientInformation" class="mt-0 pt-0">
      <v-col cols="12" class="mt-0 pt-0">
        <v-card elevation="2" :loading="loading">
          <v-toolbar v-if="!compactView" dark color="sleep">
            <v-spacer />
            <v-menu bottom left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item link @click="showTaskForm = true">
                  <v-list-item-title>Create task</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="showSnoozeClientForm = true">
                  <v-list-item-title>Snooze settings</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>

          <v-sheet v-if="!loading" color="grey lighten-4" align="center" class="py-3 ">
            <v-avatar size="80" color="error" class="align-self-center">
              <img v-if="client.profilePicture" :src="client.profilePicture" />
              <span v-else class="white--text headline">{{ client.shorthandName }}</span>
            </v-avatar>

            <h3 class="py-2">
              {{ clientInformation.user.firstName }} {{ clientInformation.user.lastName }}
              <v-tooltip v-if="clientInformation.taskPauseUntil" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-alarm-snooze</v-icon>
                </template>
                <span class="block">Tasks snoozed until: {{ clientInformation.taskPauseUntil }}</span>
                <span class="block">Reason: {{ clientInformation.taskPauseNote }}</span>
              </v-tooltip>
            </h3>

            <div class="d-inline-flex">
              <h3 class="py-2 mr-4">{{ clientInformation.user.email }}</h3>
              <v-btn icon @click="copyEmail()" class="my-auto">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" slot="activator">
                      <v-icon :color="copyEmailText == 'Copied' ? 'green' : null">mdi-content-copy</v-icon>
                    </div>
                  </template>
                  <span>{{ copyEmailText }}</span>
                </v-tooltip>
              </v-btn>
              <v-btn v-if="loginAsToken" icon @click="copyLoginAs()" class="my-auto">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" slot="activator">
                      <v-icon :color="copyLoginAsText == 'Copied' ? 'green' : null">mdi-lock-clock</v-icon>
                    </div>
                  </template>
                  <span>{{ copyLoginAsText }}</span>
                </v-tooltip>
              </v-btn>
            </div>
          </v-sheet>
          <v-card-text v-if="!compactView" align="center" justify="center">
            <div class="larger-font fake-link" @click="showPoints = true">{{ points }} Points</div>
            <client-action-bar :userId="userId" />
          </v-card-text>
          <v-card-text class="mt-0 pt-0">
            <coach
              v-if="clientInformation && clientInformation.user.userProfile.activeSubscription"
              :coach="clientInformation.user.userProfile.activeSubscription.coach"
              :subscriptionId="clientInformation.user.userProfile.activeSubscription.id"
              @coach-updated="refetchClient()"
            />
          </v-card-text>
          <v-card-title v-if="corporateChallengeTitle" class="justify-space-between"
            >{{ clientInformation.user.firstName }} {{ clientInformation.user.lastName }}
            <corporate-challenge-icon :title="corporateChallengeTitle"
          /></v-card-title>
          <ValidationObserver ref="obs">
            <v-simple-table v-if="!loading">
              <tbody>
                <tr>
                  <td>Name</td>
                  <td v-if="!edit">{{ clientInformation.user.firstName }} {{ clientInformation.user.lastName }}</td>
                  <td v-else>
                    <ValidationProvider rules="required" name="First name" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="firstName"
                        v-model="userData.firstName"
                        placeholder=""
                      ></v-text-field>
                    </ValidationProvider>
                    <ValidationProvider rules="required" name="Last name" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="lastName"
                        v-model="userData.lastName"
                        placeholder=""
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>{{ clientInformation.userStatus }}</td>
                </tr>
                <tr v-if="clientInformation.user.usertagSet && clientInformation.user.usertagSet.length > 0">
                  <td>Goals:</td>
                  <td>
                    <span v-for="userTag in clientInformation.user.usertagSet" :key="userTag.id">
                      {{ userTag.tag.title }},
                    </span>
                  </td>
                </tr>
                <tr v-if="weeklyGoal">
                  <td>Weekly Goal:</td>
                  <td v-html="weeklyGoal" />
                </tr>
                <tr v-if="clientInformation.occupation">
                  <td>Occupation</td>
                  <td>{{ clientInformation.occupation }}</td>
                </tr>
                <tr v-if="clientInformation.highestEducation">
                  <td>Highest Education</td>
                  <td>{{ clientInformation.highestEducation }}</td>
                </tr>
                <tr v-if="clientInformation.biggestObstacle">
                  <td>Biggest Obstacle</td>
                  <td>{{ clientInformation.biggestObstacle }}</td>
                </tr>
                <tr v-if="clientInformation.otherObstacleText">
                  <td>Other Obstacle</td>
                  <td>{{ clientInformation.otherObstacleText }}</td>
                </tr>
                <tr v-if="clientInformation.otherJoiningInfo">
                  <td>Other Joining Info</td>
                  <td>{{ clientInformation.otherJoiningInfo }}</td>
                </tr>
                <tr>
                  <td>City</td>
                  <td v-if="!edit">{{ clientInformation.city }}</td>
                  <td v-else>
                    <ValidationProvider name="city" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="city"
                        v-model="userData.city"
                        placeholder="City"
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td>Suburb</td>
                  <td v-if="!edit">{{ clientInformation.suburb }}</td>
                  <td v-else>
                    <ValidationProvider name="suburb" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="suburb"
                        v-model="userData.suburb"
                        placeholder="Suburb"
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr v-if="age">
                  <td>Age</td>
                  <td>{{ age }}</td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td v-if="!edit">{{ clientInformation.gender }}</td>
                  <td v-else>
                    <v-select v-model="userData.gender" :items="genderOptions"> </v-select>
                  </td>
                </tr>
                <tr>
                  <td>Pregnant</td>
                  <td>{{ clientInformation.pregnant }}</td>
                </tr>
                <tr>
                  <td>Date of Birth</td>
                  <td v-if="!edit">
                    {{ clientInformation.dateOfBirth | moment("DD MMMM YYYY") }}
                    <div v-if="birthday(clientInformation.dateOfBirth)">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon color="primary" v-bind="attrs" v-on="on" class="mb-2 ml-2">
                            mdi-cake-variant
                          </v-icon>
                        </template>
                        <span>Wish {{ clientInformation.firstName }} a happy birthday!</span>
                      </v-tooltip>
                      <span>({{ birthday(clientInformation.dateOfBirth).text }})</span>
                    </div>
                  </td>
                  <td v-else>
                    <v-menu ref="menu" v-model="menu" :close-on-content-click="false" transition="scale-transition">
                      <template v-slot:activator="{ on, attrs }">
                        <ValidationProvider mode="lazy" name="DOB" v-slot="{ errors, valid }">
                          <v-text-field
                            v-model="userData.dateOfBirth"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors"
                            :success="valid"
                          ></v-text-field>
                        </ValidationProvider>
                      </template>
                      <v-date-picker
                        ref="picker"
                        v-model="userData.dateOfBirth"
                        :max="new Date().toISOString().substr(0, 10)"
                        min="1920-01-01"
                        @change="save_date"
                      ></v-date-picker>
                    </v-menu>
                  </td>
                </tr>
                <tr>
                  <td>Date Joined</td>
                  <td>{{ formatDate(clientInformation.user.dateJoined) }} {{ joinedAgo }}</td>
                </tr>
                <tr>
                  <td>Last Active</td>
                  <td>{{ clientInformation.user.userProfile.lastActive | moment("YYYY-MM-DD HH:MM") }}</td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td v-if="!edit">{{ clientInformation.phone }}</td>
                  <td v-else>
                    <ValidationProvider rules="required" name="Phone" v-slot="{ errors }">
                      <vue-tel-input
                        mode="international"
                        :default-country="userData.country"
                        v-model="userData.phone"
                        v-on:country-changed="countryChanged"
                      />
                      <span v-if="errors.length > 0" class="error--text">The Phone field is required</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td>Fitness Level</td>
                  <td>{{ clientInformation.fitnessLevel }}</td>
                </tr>

                <tr v-if="clientInformation.hasMedicalConditions">
                  <td>Has Medical Conditions</td>
                  <td>{{ clientInformation.hasMedicalConditions }}</td>
                </tr>
                <tr v-if="clientInformation.desiredWeight">
                  <td>Weight Goal</td>
                  <td>{{ clientInformation.desiredWeight || "-" }} kg</td>
                </tr>
                <tr>
                  <td>Weight Loss Goal</td>
                  <td>{{ clientInformation.desiredWeightLoss }} kg</td>
                </tr>
                <tr v-if="clientInformation.weight">
                  <td>Current Weight</td>
                  <td>{{ clientInformation.weight || "-" }} kg</td>
                </tr>
                <tr v-if="clientInformation.dietaryRequirement">
                  <td>Dietary Requirement</td>
                  <td>{{ clientInformation.dietaryRequirement }}</td>
                </tr>
                <tr
                  v-if="
                    clientInformation.user.usernutritionpreferenceSet &&
                      clientInformation.user.usernutritionpreferenceSet.length > 0
                  "
                >
                  <td>Nutrition Preference:</td>
                  <td>
                    <span v-for="np in clientInformation.user.usernutritionpreferenceSet" :key="np.id">
                      {{ np.nutritionPreference.title }},
                    </span>
                  </td>
                </tr>
                <tr v-if="clientInformation.calories">
                  <td>Calories</td>
                  <td>{{ clientInformation.calories }}</td>
                </tr>
                <tr>
                  <td>Calculated Serving Size</td>
                  <td v-if="!edit">{{ clientInformation.calculatedServingSize }}</td>
                  <td v-else>
                    <ValidationProvider rules="required" name="caculatedServingSize" v-slot="{ errors, valid }">
                      <v-select
                        :error-messages="errors"
                        :success="valid"
                        ref="caculatedServingSize"
                        v-model="userData.calculatedServingSize"
                        :items="servingSizeOptions"
                        placeholder="Calculated Serving Size"
                      />
                    </ValidationProvider>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-alert v-if="showInvalid" type="error" text class="ma-4">Some required fields are missing</v-alert>
            <v-card-actions v-if="!compactView">
              <v-btn v-if="edit" @click="cancel()" :loading="saving">Cancel</v-btn>
              <v-spacer />
              <v-btn v-if="edit" :loading="saving" @click="save()">Save</v-btn>
              <v-btn v-if="!edit" @click="edit = true">Edit</v-btn>
            </v-card-actions>
          </ValidationObserver>
        </v-card>
      </v-col>
      <v-dialog v-model="showPoints" width="900" class="limited-height" transition="dialog-bottom-transition">
        <client-reward-point-events :userId="userId" />
      </v-dialog>
      <v-dialog
        v-model="showTaskForm"
        persistent
        class="limited-height"
        width="900"
        transition="dialog-bottom-transition"
      >
        <task-form :userId="userId" @close="closeModal" />
      </v-dialog>
      <v-dialog
        v-model="showSnoozeClientForm"
        persistent
        class="limited-height"
        width="900"
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-toolbar dark color="secondary">
            <v-toolbar-title>
              Snooze client
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="closeModal()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <snooze-client
              :userId="userId"
              :snoozedUntil="clientInformation.taskPauseUntil"
              @task-pause-until-updated="handleSnooze"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import {
  CLIENT_INFORMATION_QUERY,
  LOGIN_AS_TOKEN_QUERY,
  UPDATE_PROFILE_MUTATION,
} from "@/constants/graphql/client/client";
import { USER_TOTAL_POINTS_QUERY } from "@/graphql/queries/user";
import { YYYYMMDD_readable } from "@/lib/fancy_date";
import BirthdayMixin from "@/components/shared/mixins/BirthdayMixin";
import ClientRewardPointEvents from "@/components/client/blocks/activity/ClientRewardPointEvents";
import ClientActionBar from "@/components/client/blocks/ClientActionBar";
import TaskForm from "@/components/task/components/TaskForm";
import SnoozeClient from "@/components/client/blocks/SnoozeClient";
import Coach from "@/components/client/blocks/Coach";
import CorporateChallengeIcon from "@/components/shared/user/CorporateChallengeIcon";

export default {
  name: "UserInfromation",
  mixins: [BirthdayMixin],
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
    compactView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    ClientRewardPointEvents,
    ClientActionBar,
    TaskForm,
    SnoozeClient,
    Coach,
    CorporateChallengeIcon,
  },
  data() {
    return {
      loading: true,
      activeProgram: null,
      points: 0,
      saving: false,
      edit: false,
      menu: false,
      showInvalid: false,
      showPoints: false,
      showTaskForm: false,
      showSnoozeClientForm: false,
      copyEmailText: "Copy email",
      copyLoginAsText: "Copy login-as password",
      servingSizeOptions: [
        { text: "", value: null },
        { text: "Lite", value: "LITE" },
        { text: "Small", value: "SMALL" },
        { text: "Medium", value: "MEDIUM" },
        { text: "Standard", value: "STANDARD" },
      ],
      client: {
        name: "",
        shorthandName: "",
        profilePicture: null,
      },
      genderOptions: [
        { text: "", value: null },
        { text: "Female", value: "FEMALE" },
        { text: "Male", value: "MALE" },
      ],
      data: {
        userId: null,
        dateOfBirth: null,
        firstName: "",
        lastName: "",
        gender: null,
        phone: "",
        suburb: "",
        city: "",
        email: "",
      },
    };
  },
  computed: {
    weeklyGoal() {
      let text = "";
      const weeklyGoal = JSON.parse(this.clientInformation?.weeklyGoal);
      if (weeklyGoal) {
        text = `Exercise: ${weeklyGoal.targets?.weekly_exercise} days<br/>Nutrition: ${weeklyGoal.targets?.weekly_exercise} days`;
        if (weeklyGoal.targets?.focus) {
          text += `<br>Focus: ${weeklyGoal.targets?.focus.title}`;
        }
      }
      return text;
    },
    corporateChallengeTitle() {
      return this.clientInformation?.user?.userProfile.activeSubscription?.corporateChallenge?.title;
    },
    age() {
      if (this.clientInformation?.dateOfBirth) {
        let dob = this.$moment(this.clientInformation.dateOfBirth).format("YYYY-MM-DD");
        let age = this.$moment().diff(dob, "years");

        return age;
      }

      return "";
    },
    joinedAgo() {
      if (this.clientInformation?.user.dateJoined) {
        let dateJoined = this.$moment(this.clientInformation.user.dateJoined);
        return `(${dateJoined.fromNow()})`;
      }

      return "";
    },
  },
  methods: {
    async copyEmail() {
      await navigator.clipboard.writeText(this.clientInformation.user.username);
      this.copyEmailText = "Copied";
      this.copyLoginAsText = "Copy Login-as password";
    },
    async copyLoginAs() {
      await navigator.clipboard.writeText(this.loginAsToken);
      this.copyLoginAsText = "Copied";
      this.copyEmailText = "Copy email";
    },
    refetchClient() {
      this.$apollo.queries.clientInformation.refetch(this.userId);
    },
    showModal() {
      this.showPoints = true;
    },
    handleSnooze() {
      this.showSnoozeClientForm = false;
      this.$apollo.queries.clientInformation.refetch();
    },
    closeModal() {
      this.showSnoozeClientForm = false;
      this.showTaskForm = false;
    },
    formatDate(date) {
      return YYYYMMDD_readable(date);
    },
    countryChanged(country) {
      this.userData.country = country.iso2;
    },
    save_date(date) {
      this.$refs.menu.save(date);
    },
    cancel() {
      this.edit = false;
      let client = this.clientInformation;
      this.userData = {
        userId: this.userId,
        dateOfBirth: client.dateOfBirth,
        firstName: client.firstName,
        lastName: client.lastName,
        gender: client.gender,
        phone: client.phone,
        whatsappJid: client.whatsappJid,
        alternateWhatsappJid: client.alternateWhatsappJid,
        suburb: client.suburb,
        city: client.city,
        dietaryRequirement: client.dietaryRequirement,
        email: client.email,
        height: client.height,
        calculatedServingSize: client.calculatedServingSize,
      };
    },
    save() {
      this.$refs.obs.validate().then((valid) => {
        if (!valid) {
          this.showInvalid = true;
        } else {
          this.showInvalid = false;
          this.saving = true;

          this.$apollo
            .mutate({
              mutation: UPDATE_PROFILE_MUTATION,
              variables: {
                userProfileInput: this.userData,
              },
              refetchQueries: ["clientInformation"],
            })
            .then((response) => {
              this.saving = false;
              if (response.data.updateProfile.ok) {
                this.edit = false;
                this.$toasted.success("Information updated", {
                  icon: "arm-flex",
                  duration: 2000,
                  position: "bottom-center",
                });
              } else {
                this.$toasted.error("Unable to update your Information. Sorry.", {
                  icon: "alert",
                  duration: 2000,
                  position: "bottom-center",
                });
              }
            });
        }
      });
    },
  },
  apollo: {
    clientInformation: {
      query: CLIENT_INFORMATION_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;
          let client = response.data.clientInformation;
          this.client.shorthandName = `${client.user.firstName[0]} ${client.user.lastName[0]}`;
          this.client.profilePicture = client.profilePicture;
          this.userData = {
            userId: this.userId,
            dateOfBirth: client.dateOfBirth,
            firstName: client.user.firstName,
            lastName: client.user.lastName,
            gender: client.gender,
            phone: client.phone,
            suburb: client.suburb,
            city: client.city,
            email: client.user.email,
            calculatedServingSize: client.calculatedServingSize,
            country: client.country.code ? client.country.code : "ZA",
          };
        }
      },
    },
    loginAsToken: {
      query: LOGIN_AS_TOKEN_QUERY,
    },
    rewardPoints: {
      query: USER_TOTAL_POINTS_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;
          this.points = response.data?.rewardPoints?.totalPoints ?? 0;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.larger-font {
  font-size: 45px;
}
</style>
